// src/components/ErrorBoundary.js
import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // In production, send to error tracking service (e.g. Sentry)
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem 1rem",
          background: "var(--bg-void)",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "480px" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "rgba(245,101,101,0.12)",
              border: "2px solid var(--status-danger)",
              marginBottom: "1.25rem",
            }}
          >
            <AlertTriangle size={28} color="var(--status-danger)" />
          </div>

          <h2
            style={{
              fontSize: "clamp(1.1rem, 2.5vw, 1.5rem)",
              fontWeight: 800,
              color: "var(--text-primary)",
              margin: "0 0 0.5rem",
            }}
          >
            Something went wrong
          </h2>
          <p
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--text-muted)",
              lineHeight: 1.7,
              marginBottom: "1.5rem",
            }}
          >
            An unexpected error occurred while loading this section.
            Try refreshing the page or returning to the home screen.
          </p>

          {this.state.error?.message && (
            <pre
              style={{
                fontSize: "0.72rem",
                color: "var(--status-danger)",
                background: "rgba(245,101,101,0.07)",
                border: "1px solid rgba(245,101,101,0.2)",
                borderRadius: "var(--radius-sm)",
                padding: "0.75rem",
                textAlign: "left",
                overflowX: "auto",
                marginBottom: "1.5rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {this.state.error.message}
            </pre>
          )}

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={this.handleReset}
              className="bu-btn bu-btn-primary"
              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <RefreshCw size={14} />
              Try Again
            </button>
            <a
              href="/"
              className="bu-btn bu-btn-ghost"
              style={{ textDecoration: "none" }}
            >
              Go to Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
